import React, { useState } from 'react';
import { 
  FileText, CheckCircle2, Calendar,
  Building2, DollarSign, X, Clock,
  FileCheck, Send, ChevronDown, ChevronRight,
  Shield, LayoutDashboard,
  MessageSquare, Gavel, Lock, HelpCircle, Menu
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

// Sample data for preview
const sampleProjects = [
  { id: '1', name: '38th Street Build', address: '123 Main St, Cedar Rapids, IA', bidCount: 3 },
  { id: '2', name: 'New Build 38th', address: '456 Oak Ave, Cedar Rapids, IA', bidCount: 6 },
  { id: '3', name: 'Downtown Renovation', address: '789 Center St, Cedar Rapids, IA', bidCount: 2 },
];

const sampleBids = [
  { id: '1', title: 'Test Flooring Bid Request', trade_type: 'Flooring', due_date: '2026-03-01', scope: 'Install hardwood flooring throughout main level' },
  { id: '2', title: 'Drywall Bid Request', trade_type: 'Drywall', due_date: '2026-02-10', scope: 'Hang and finish drywall per plans' },
  { id: '3', title: 'Electrical Rough-In', trade_type: 'Electrical', due_date: '2026-02-15', scope: 'Complete electrical rough-in per approved plans' },
];

// Tab definitions
const portalTabs = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'invoices', label: 'Invoices', icon: DollarSign },
  { id: 'bids', label: 'Bids', icon: Gavel },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'schedule', label: 'Schedule', icon: Calendar },
  { id: 'documents', label: 'Documents', icon: FileCheck },
];

// ============================================
// OPTION A: Hamburger Menu Sidebar (Slides Out)
// ============================================
const OptionAHamburger = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeTab, setActiveTab] = useState('bids');
  const [selectedProject, setSelectedProject] = useState(sampleProjects[0].id);

  return (
    <div className="min-h-screen bg-slate-100 relative">
      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Slide-out Sidebar */}
      <aside className={`fixed left-0 top-0 h-full w-64 bg-slate-900 z-50 transform transition-transform duration-300 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Close button */}
        <button 
          onClick={() => setSidebarOpen(false)}
          className="absolute top-4 right-4 text-white"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Logo */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div>
              <h1 className="font-bold text-lg text-white">DrawReady</h1>
              <p className="text-xs text-slate-400">Contractor Portal</p>
            </div>
          </div>
        </div>

        {/* Contractor Info */}
        <div className="px-4 py-3 border-b border-slate-700">
          <h2 className="font-bold text-white">ABC Plumbing LLC</h2>
          <div className="flex gap-2 mt-2">
            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">W-9: Req</span>
            <span className="text-xs px-2 py-0.5 rounded bg-red-500/20 text-red-400">COI: Req</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          <ul className="space-y-1 px-3">
            {portalTabs.map(tab => (
              <li key={tab.id}>
                <button
                  onClick={() => { setActiveTab(tab.id); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    activeTab === tab.id ? 'bg-orange-600 text-white' : 'text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <tab.icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              </li>
            ))}
          </ul>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="min-h-screen">
        {/* Top Header with Hamburger */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white sticky top-0 z-30">
          <div className="px-4 py-4 flex items-center gap-3">
            <button 
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-white/10 rounded-lg"
            >
              <Menu className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-xl font-bold">Contractor Portal</h1>
              <p className="text-orange-100 text-sm">ABC Plumbing LLC</p>
            </div>
          </div>
        </div>

        {/* Bids Content with Dropdown Selector */}
        <main className="p-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="w-5 h-5 text-orange-600" />
                Open Bid Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Project Dropdown Selector */}
              <div className="mb-6">
                <label className="text-sm font-medium text-slate-700 mb-2 block">Select Project</label>
                <Select value={selectedProject} onValueChange={setSelectedProject}>
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sampleProjects.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        <div className="flex items-center justify-between w-full">
                          <span>{p.name}</span>
                          <Badge variant="secondary" className="ml-2">{p.bidCount} bids</Badge>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Bids for selected project */}
              <div className="space-y-3">
                {sampleBids.map((bid, idx) => (
                  <div key={bid.id} className="p-4 border rounded-lg hover:bg-slate-50">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{bid.title}</h4>
                          <Badge variant="secondary">{bid.trade_type}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{bid.scope}</p>
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due: {bid.due_date}
                        </span>
                      </div>
                      <Button size="sm" className="bg-orange-600 hover:bg-orange-700">
                        Submit Bid
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
      
      {/* Label */}
      <div className="fixed bottom-4 left-4 bg-black text-white px-3 py-2 rounded-lg text-sm font-bold z-50">
        OPTION A: Hamburger + Dropdown
      </div>
    </div>
  );
};

// ============================================
// OPTION B: Bottom Navigation + Card Tiles
// ============================================
const OptionBBottomNav = () => {
  const [activeTab, setActiveTab] = useState('bids');
  const [selectedProject, setSelectedProject] = useState(null);

  const bottomTabs = portalTabs.slice(0, 5); // Limit to 5 for bottom nav

  return (
    <div className="min-h-screen bg-slate-100 pb-20">
      {/* Top Header */}
      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white sticky top-0 z-30">
        <div className="px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <span className="text-white font-bold">D</span>
            </div>
            <div>
              <h1 className="text-xl font-bold">Contractor Portal</h1>
              <p className="text-orange-100 text-sm">ABC Plumbing LLC</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Gavel className="w-5 h-5 text-orange-600" />
              Open Bid Requests
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!selectedProject ? (
              /* Project Card Tiles */
              <div className="space-y-3">
                <p className="text-sm text-slate-600 mb-4">Select a project to view open bids:</p>
                {sampleProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => setSelectedProject(project)}
                    className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-slate-800 rounded-lg flex items-center justify-center">
                        <Building2 className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-900">{project.name}</h3>
                        <p className="text-sm text-slate-500">{project.address}</p>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-orange-500 text-white">{project.bidCount}</Badge>
                        <p className="text-xs text-slate-500 mt-1">open bids</p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              /* Bids for selected project */
              <div>
                <button 
                  onClick={() => setSelectedProject(null)}
                  className="flex items-center gap-2 text-orange-600 mb-4 hover:underline"
                >
                  <ChevronDown className="w-4 h-4 rotate-90" />
                  Back to projects
                </button>
                
                <div className="bg-slate-800 text-white rounded-lg p-4 mb-4">
                  <h3 className="font-bold">{selectedProject.name}</h3>
                  <p className="text-slate-300 text-sm">{selectedProject.address}</p>
                </div>

                <div className="space-y-3">
                  {sampleBids.map((bid, idx) => (
                    <div key={bid.id} className="p-4 border rounded-lg hover:bg-slate-50">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold">{bid.title}</h4>
                          <Badge variant="secondary" className="mt-1">{bid.trade_type}</Badge>
                          <p className="text-sm text-slate-600 mt-2">{bid.scope}</p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between mt-3 pt-3 border-t">
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due: {bid.due_date}
                        </span>
                        <Button size="sm" className="bg-orange-600 hover:bg-orange-700">
                          Submit Bid
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-40">
        <div className="flex justify-around py-2">
          {bottomTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center gap-1 px-3 py-2 rounded-lg transition-all ${
                activeTab === tab.id 
                  ? 'text-orange-600' 
                  : 'text-slate-500'
              }`}
            >
              <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-orange-600' : ''}`} />
              <span className="text-xs font-medium">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>
      
      {/* Label */}
      <div className="fixed bottom-24 left-4 bg-black text-white px-3 py-2 rounded-lg text-sm font-bold z-50">
        OPTION B: Bottom Nav + Card Tiles
      </div>
    </div>
  );
};

// ============================================
// OPTION C: Always-Visible Compact Sidebar + Project Tabs (BLUE THEME)
// ============================================
const OptionCCompactSidebar = () => {
  const [activeTab, setActiveTab] = useState('bids');
  const [selectedProject, setSelectedProject] = useState(sampleProjects[0].id);

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Compact Always-Visible Sidebar */}
      <aside className="w-16 bg-slate-900 flex flex-col items-center py-4 fixed left-0 top-0 h-full z-40">
        {/* Logo */}
        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
          <span className="text-white font-bold">D</span>
        </div>

        {/* Navigation Icons */}
        <nav className="flex-1 flex flex-col gap-2">
          {portalTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${
                activeTab === tab.id 
                  ? 'bg-blue-600 text-white' 
                  : 'text-slate-400 hover:bg-slate-800 hover:text-white'
              }`}
              title={tab.label}
            >
              <tab.icon className="w-5 h-5" />
            </button>
          ))}
        </nav>

        {/* Bottom icons */}
        <div className="flex flex-col gap-2">
          <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-800 hover:text-white">
            <Lock className="w-5 h-5" />
          </button>
          <button className="w-10 h-10 rounded-lg flex items-center justify-center text-slate-400 hover:bg-slate-800 hover:text-white">
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 ml-16">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white sticky top-0 z-30">
          <div className="px-4 py-4">
            <h1 className="text-xl font-bold">Contractor Portal</h1>
            <p className="text-blue-100 text-sm">ABC Plumbing LLC</p>
          </div>
        </div>

        {/* Bids Content */}
        <main className="p-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Gavel className="w-5 h-5 text-blue-600" />
                Open Bid Requests
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Project Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
                {sampleProjects.map(project => (
                  <button
                    key={project.id}
                    onClick={() => setSelectedProject(project.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                      selectedProject === project.id
                        ? 'bg-blue-600 text-white'
                        : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    }`}
                  >
                    <Building2 className="w-4 h-4" />
                    {project.name}
                    <Badge className={selectedProject === project.id ? 'bg-blue-400' : 'bg-slate-300 text-slate-700'}>
                      {project.bidCount}
                    </Badge>
                  </button>
                ))}
              </div>

              {/* Bids List */}
              <div className="space-y-3">
                {sampleBids.map((bid, idx) => (
                  <div key={bid.id} className="p-4 border rounded-lg hover:bg-slate-50 border-l-4 border-l-transparent hover:border-l-blue-500">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center font-bold text-sm">
                        {idx + 1}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold">{bid.title}</h4>
                          <Badge variant="secondary">{bid.trade_type}</Badge>
                        </div>
                        <p className="text-sm text-slate-600 mb-2">{bid.scope}</p>
                        <span className="text-xs text-red-600 flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          Due: {bid.due_date}
                        </span>
                      </div>
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700">
                        <Send className="w-4 h-4 mr-1" />
                        Submit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </main>
      </div>
      
      {/* Label */}
      <div className="fixed bottom-4 left-20 bg-black text-white px-3 py-2 rounded-lg text-sm font-bold z-50">
        OPTION C: Compact Sidebar + Project Tabs (Blue Theme)
      </div>
    </div>
  );
};

// ============================================
// Main Preview Page - Shows All Options
// ============================================
export default function ContractorPortalPreview() {
  const [viewOption, setViewOption] = useState('all');

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Option Selector */}
      <div className="fixed top-0 left-0 right-0 bg-slate-800 z-[100] px-4 py-3 flex items-center gap-4">
        <span className="text-white font-bold">Preview Options:</span>
        <div className="flex gap-2">
          {['all', 'A', 'B', 'C'].map(opt => (
            <button
              key={opt}
              onClick={() => setViewOption(opt)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                viewOption === opt 
                  ? 'bg-orange-600 text-white' 
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {opt === 'all' ? 'View All' : `Option ${opt}`}
            </button>
          ))}
        </div>
      </div>

      {/* Preview Content */}
      <div className="pt-16">
        {viewOption === 'all' ? (
          <div className="grid grid-cols-3 gap-1 h-[calc(100vh-64px)]">
            <div className="overflow-hidden border-r border-slate-700">
              <div className="h-full overflow-y-auto">
                <OptionAHamburger />
              </div>
            </div>
            <div className="overflow-hidden border-r border-slate-700">
              <div className="h-full overflow-y-auto">
                <OptionBBottomNav />
              </div>
            </div>
            <div className="overflow-hidden">
              <div className="h-full overflow-y-auto">
                <OptionCCompactSidebar />
              </div>
            </div>
          </div>
        ) : viewOption === 'A' ? (
          <OptionAHamburger />
        ) : viewOption === 'B' ? (
          <OptionBBottomNav />
        ) : (
          <OptionCCompactSidebar />
        )}
      </div>
    </div>
  );
}
